import React, { useEffect, useState } from "react";
import { useBrand } from "../../utils/useBrand";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useCompatibility } from "./useCompatibility";
import { ConnectionRequest } from "./types";
import {
  ArrowLeft,
  Heart,
  Clock,
  CheckCircle,
  XCircle,
  MessageCircle,
  FileText,
  User,
  Mail,
  Calendar,
  Filter,
  Loader2,
  Send,
  Inbox,
  Users,
  Sparkles,
} from "lucide-react";

type FilterType = "all" | "pending" | "accepted" | "rejected";

export const RequestHistory: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const {
    loading,
    history,
    fetchHistory,
    acceptConnectionRequest,
    rejectConnectionRequest,
  } = useCompatibility();

  const [filter, setFilter] = useState<FilterType>("all");
  const [actioningId, setActioningId] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory(filter === "all" ? undefined : filter).catch(() =>
      toast.error("Failed to load request history."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleAccept = async (requestId: string, otherUserName: string) => {
    setActioningId(requestId);
    try {
      const result = await acceptConnectionRequest(requestId);
      if (result.success) {
        toast.success("✨ Connected successfully!");
        navigate(`/compatibility-synastry/${requestId}`, {
          state: { otherUserName },
        });
      }
    } catch (error) {
      toast.error("Failed to accept request. Please try again.");
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActioningId(requestId);
    try {
      await rejectConnectionRequest(requestId);
      toast.success("Request declined");
      await fetchHistory(filter === "all" ? undefined : filter);
    } catch (error) {
      toast.error("Failed to reject request. Please try again.");
    } finally {
      setActioningId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "accepted":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "rejected":
        return "bg-rose-100 text-rose-700 border-rose-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getFilterIcon = (filterValue: FilterType) => {
    switch (filterValue) {
      case "all":
        return <Users className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "pending":
        return "Pending";
      case "accepted":
        return "Connected";
      case "rejected":
        return "Declined";
      default:
        return status;
    }
  };

  const renderItem = (item: ConnectionRequest) => {
    const { otherUser, isSender } = item;
    const isActioning = actioningId === item._id;

    return (
      <div
        key={item._id}
        className="group bg-white/80 backdrop-blur-xl rounded-3xl p-5 shadow-lg shadow-rose-100/30 border border-white/50 hover:shadow-2xl hover:shadow-rose-200/50 transition-all duration-300 hover:scale-[1.01]"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-rose-400 to-purple-400 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-rose-200 group-hover:shadow-xl group-hover:shadow-rose-300 transition-all duration-200">
                {otherUser.username.charAt(0).toUpperCase()}
              </div>
              {item.status === "accepted" && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 group-hover:text-rose-400 transition-colors">
                {otherUser.username}
              </h3>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  {isSender ? (
                    <Send className="w-3 h-3" />
                  ) : (
                    <Inbox className="w-3 h-3" />
                  )}
                  {isSender ? "Sent" : "Received"}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(item.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${getStatusColor(item.status)}`}
          >
            {getStatusIcon(item.status)}
            <span className="text-xs font-semibold">
              {getStatusLabel(item.status)}
            </span>
          </div>
        </div>

        {item.message && (
          <div className="mt-3 px-4 py-3 bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-xl border border-gray-100/50">
            <p className="text-sm text-gray-600 italic flex items-start gap-2">
              <span className="text-rose-400">"</span>
              {item.message}
              <span className="text-rose-400">"</span>
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100/50">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(item.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>

          <div className="flex flex-wrap gap-2">
            {item.status === "pending" && !isSender && (
              <>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-emerald-400 to-emerald-500 text-white font-medium rounded-xl shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-300 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleAccept(item._id, otherUser.username)}
                  disabled={isActioning}
                >
                  {isActioning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Accept
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-gray-100 text-rose-500 font-medium rounded-xl hover:bg-gray-200 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleReject(item._id)}
                  disabled={isActioning}
                >
                  {isActioning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Decline
                </button>
              </>
            )}

            {item.status === "pending" && isSender && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl border border-amber-200">
                <Clock className="w-4 h-4 text-amber-500 animate-pulse" />
                <span className="text-sm font-medium text-amber-600">
                  Waiting for response...
                </span>
              </div>
            )}

            {item.status === "accepted" && (
              <>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 hover:scale-105 transition-all duration-200"
                  onClick={() =>
                    navigate(`/compatibility-synastry/${item._id}`)
                  }
                >
                  <FileText className="w-4 h-4" />
                  Report
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-rose-400 to-purple-400 text-white font-medium rounded-xl shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-105 transition-all duration-200"
                  onClick={() =>
                    navigate(`/compatibility-chat/${item._id}`, {
                      state: { otherUserName: otherUser.username },
                    })
                  }
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filters: Array<{ label: string; value: FilterType }> = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Accepted", value: "accepted" },
    { label: "Rejected", value: "rejected" },
  ];

  const getFilterCount = (filterValue: FilterType) => {
    if (filterValue === "all") return history.length;
    return history.filter((item) => item.status === filterValue).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-rose-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/10 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 mb-6 shadow-lg shadow-rose-100/30 border border-white/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/matches"
                className="p-2 hover:bg-rose-50 rounded-xl transition-all duration-200 group"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-rose-400 transition-colors" />
              </Link>
              <div>
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-rose-400 to-purple-400 p-2 rounded-xl shadow-lg shadow-rose-200">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Connection History
                  </h1>
                </div>
                <p className="text-sm text-gray-500 ml-12 mt-1">
                  {history.length > 0
                    ? `✨ ${history.length} connection${history.length > 1 ? "s" : ""} in your journey`
                    : "🌟 Your connection story begins here"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Sparkles className="w-4 h-4 text-rose-400" />
              <span>{brand.name}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-4 mb-6 shadow-lg shadow-rose-100/30 border border-white/50">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 mr-2 text-gray-500">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  filter === f.value
                    ? "bg-gradient-to-r from-rose-400 to-purple-400 text-white shadow-lg shadow-rose-200 scale-105"
                    : "bg-gray-50/50 text-gray-600 hover:bg-gray-100 hover:scale-105"
                }`}
                onClick={() => setFilter(f.value)}
              >
                {getFilterIcon(f.value)}
                {f.label}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    filter === f.value
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {getFilterCount(f.value)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && history.length === 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-rose-100/30 border border-white/50">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-rose-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-rose-400 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <span className="mt-4 text-gray-500 font-medium">
                Loading your connections...
              </span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && history.length === 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-rose-100/30 border border-white/50">
            <div className="w-24 h-24 bg-gradient-to-r from-rose-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-rose-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              No Connections Yet
            </h3>
            <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
              Your journey of connection is just beginning. Start exploring
              matches and build meaningful relationships.
            </p>
            <Link
              to="/matches"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-gradient-to-r from-rose-400 to-purple-400 text-white font-medium rounded-xl shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-105 transition-all duration-200"
            >
              <Users className="w-5 h-5" />
              Find Matches
            </Link>
          </div>
        )}

        {/* History List */}
        <div className="space-y-4">
          {history.map((item) => renderItem(item))}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="text-center mt-8">
            <p className="text-xs text-gray-400 flex items-center justify-center gap-2">
              <Heart className="w-3 h-3 text-rose-400" />
              <span>Every connection is a step in your journey</span>
              <Heart className="w-3 h-3 text-rose-400" />
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestHistory;
